import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { CheckIcon, EyeClosedIcon, EyeIcon, QuestionMarkIcon } from '@phosphor-icons/react';
import { cva } from 'class-variance-authority';
import { IconButton } from './icon-button';
import { cn } from './lib/cn';
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from './popover';
import { Text } from './text';
const inputVariants = cva(`flex h-12 px-3 transition-all w-full text-[14px] text-role-on-background rounded-2xl bg-role-background
placeholder-role-outline-subtle
border border-role-outline-subtle
not-placeholder-shown:border-role-info-outline
hover:border-role-outline-subtle hover:bg-[#f5f5f5]
focus-visible:bg-role-background focus-visible:border-role-outline-subtle focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-role-info-outline
disabled:cursor-not-allowed disabled:border-role-disabled disabled:bg-role-disabled disabled:text-role-on-disabled
file:border-0 file:bg-transparent file:text-[14px] file:font-medium
shadow-none outline-hidden focus-visible:shadow-none
box-border`, {
  variants: {
    error: {
      true: 'border border-red-dark placeholder:text-red-dark focus-visible:border-red-dark',
      false: ''
    }
  },
  defaultVariants: {}
});
export interface InputProps extends React.ComponentProps<'input'> {
  error?: boolean;
}
function InputPasswordPopoverContent(t0) {
  const $ = _c(15);
  const {
    rule,
    text
  } = t0;
  const t1 = rule ? "border-role-success-container bg-role-success-container" : "border-role-disabled text-role-on-disabled";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex h-4 w-4 items-center justify-center rounded-2xl border", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== rule) {
    t3 = rule ? <CheckIcon className="text-role-on-success-container" size={10} weight="bold" /> : null;
    $[2] = rule;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = rule ? "text-role-on-background" : "text-role-on-background-subtle";
  let t6;
  if ($[7] !== t5) {
    t6 = cn(t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6 || $[10] !== text) {
    t7 = <Text body="xs" className={t6}>{text}</Text>;
    $[9] = t6;
    $[10] = text;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t7) {
    t8 = <li className="flex items-center justify-center gap-2">{t4}{t7}</li>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function InputPasswordTooltip(t0) {
  const $ = _c(42);
  const {
    value
  } = t0;
  const charsOrMore = String(value ?? "").length >= 8;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /(?=.*?[A-Z])/g;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== value) {
    t2 = t1.test(String(value ?? ""));
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const atLeastOneUppercase = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = /(?=.*?[a-z])/g;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== value) {
    t4 = t3.test(String(value ?? ""));
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const atLeastOneLowercase = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = /(?=.*?[0-9])/g;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== value) {
    t6 = t5.test(String(value ?? ""));
    $[7] = value;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const atLeastOneNumeric = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = /(.*[!@#$%^&*(),.?":{}|<>].*)/g;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== value) {
    t8 = t7.test(String(value ?? ""));
    $[10] = value;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const atLeastOneSpecial = t8;
  const isPasswordValid = charsOrMore && atLeastOneUppercase && atLeastOneLowercase && atLeastOneNumeric && atLeastOneSpecial;
  const isPasswordInfo = value?.length === 0 || isPasswordValid;
  const t9 = isPasswordInfo ? "border-role-info" : "border-role-error";
  let t10;
  if ($[12] !== t9) {
    t10 = cn(t9);
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = isPasswordInfo ? "info" : "error";
  const t12 = isPasswordInfo ? "text-role-info" : "text-role-error";
  let t13;
  if ($[14] !== t12) {
    t13 = cn(t12);
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t13) {
    t14 = <QuestionMarkIcon className={t13} size={12} weight="bold" />;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t14) {
    t15 = <PopoverTrigger asChild={true}><IconButton appearance="outlined" className={t10} color={t11} rounded="full" size="xs" type="button">{t14}</IconButton></PopoverTrigger>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <PopoverHeader><PopoverTitle>Password must contain at least:</PopoverTitle></PopoverHeader>;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== charsOrMore) {
    t17 = <InputPasswordPopoverContent rule={charsOrMore} text="8 Characters" />;
    $[23] = charsOrMore;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== atLeastOneUppercase) {
    t18 = <InputPasswordPopoverContent rule={atLeastOneUppercase} text="1 Upper case character" />;
    $[25] = atLeastOneUppercase;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== atLeastOneLowercase) {
    t19 = <InputPasswordPopoverContent rule={atLeastOneLowercase} text="1 Lower case character" />;
    $[27] = atLeastOneLowercase;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== atLeastOneNumeric) {
    t20 = <InputPasswordPopoverContent rule={atLeastOneNumeric} text="1 Number" />;
    $[29] = atLeastOneNumeric;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== atLeastOneSpecial) {
    t21 = <InputPasswordPopoverContent rule={atLeastOneSpecial} text="1 Special Character (e.g !, @, #)" />;
    $[31] = atLeastOneSpecial;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t17 || $[34] !== t18 || $[35] !== t19 || $[36] !== t20 || $[37] !== t21) {
    t22 = <PopoverContent align="center">{t16}<ul className="flex flex-col items-start justify-center gap-2">{t17}{t18}{t19}{t20}{t21}</ul></PopoverContent>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t15 || $[40] !== t22) {
    t23 = <Popover>{t15}{t22}</Popover>;
    $[39] = t15;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  return t23;
}
function InputPassword(t0) {
  const $ = _c(16);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      error: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const error = t1 === undefined ? false : t1;
  const [showPassword, setShowPassword] = useState(false);
  let t2;
  if ($[4] !== className || $[5] !== error) {
    t2 = cn(inputVariants({
      error,
      className
    }));
    $[4] = className;
    $[5] = error;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = showPassword ? "text" : "password";
  let t4;
  if ($[7] !== props || $[8] !== t2 || $[9] !== t3) {
    t4 = <input className={t2} data-slot="input-password" {...props} type={t3} />;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== showPassword) {
    t5 = <div className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-gray-400">{showPassword ? <EyeClosedIcon className="size-4" onClick={() => setShowPassword(false)} /> : <EyeIcon className="size-4" onClick={() => setShowPassword(true)} />}</div>;
    $[11] = showPassword;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className="relative">{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
export { InputPassword, InputPasswordTooltip };